import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import {
  useInquiry,
  useInquiryMessages,
  useSendMessage,
  useMarkMessagesRead,
  useUpdateInquiryStatus,
  useDeleteMessage,
  InquiryMessageWithSender,
} from "../../hooks/useApi";
import { useAuth } from "../../AuthContext";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";

const InquiryChatPage: React.FC = () => {
  const { inquiryId } = useParams<{ inquiryId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [message, setMessage] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ユーザー情報取得
  const userClaims = currentUser
    ? (currentUser as any).reloadUserInfo?.customAttributes
    : null;
  const roles: string[] = userClaims ? JSON.parse(userClaims).roles || [] : [];
  const isStaffOrAdmin = roles.includes("staff") || roles.includes("admin");
  const userId = currentUser?.uid || "";

  // データ取得
  const {
    data: inquiry,
    isLoading: inquiryLoading,
    error: inquiryError,
  } = useInquiry(inquiryId || "");
  const {
    data: messages,
    isLoading: messagesLoading,
    error: messagesError,
  } = useInquiryMessages(inquiryId || "");

  // Mutation hooks
  const sendMessageMutation = useSendMessage(inquiryId || "");
  const markReadMutation = useMarkMessagesRead(inquiryId || "");
  const updateStatusMutation = useUpdateInquiryStatus(inquiryId || "");
  const deleteMessageMutation = useDeleteMessage(inquiryId || "");

  // 自動スクロール
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 既読処理
  useEffect(() => {
    if (messages && messages.length > 0) {
      const hasUnreadMessages = messages.some(
        (msg) => !msg.is_read && msg.sender_firebase_uid !== userId
      );
      if (hasUnreadMessages) {
        markReadMutation.mutate();
      }
    }
  }, [messages, userId, markReadMutation.mutate,markReadMutation]);

  // SSE接続
  useEffect(() => {
    if (!inquiryId || !currentUser) return;

    const url = `${process.env.REACT_APP_API_BASE_URL}/inquiries/${inquiryId}/events`;

    // EventSourceは標準ではヘッダーをサポートしていないため、
    // クエリパラメータでトークンを渡すか、cookieを使う必要があるが、
    // ここでは簡易的にEventSourcePolyfillを使うか、
    // あるいはバックエンドが認証なしで接続を受け入れるか（セキュリティリスクあり）、
    // または標準のEventSourceで接続し、バックエンドがCookie認証をサポートしているか確認。
    // 現状のバックエンド実装では `AuthenticatedUser` extractor を使用しているため、
    // Authorization header が必要。
    // 標準の EventSource ではヘッダーを設定できないため、
    // fetch-event-source などのライブラリを使うのが一般的。
    // しかし、今回は依存関係を追加せずに実装するため、
    // 一旦、バックエンドのSSEエンドポイントを認証なしにするか、
    // クエリパラメータでトークンを受け取るように変更する必要があるかもしれません。
    //
    // ただし、今回は `event-source-polyfill` などを導入していないため、
    // 既存の依存関係で何とかする必要があります。
    // `fetch` を使ってストリームを読み込む方法もあります。

    // 今回は `fetch` で実装します。
    const controller = new AbortController();

    const connectSSE = async () => {
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          console.error("SSE connection failed:", response.statusText);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = line.slice(6);
              try {
                const newMessage: InquiryMessageWithSender = JSON.parse(data);

                // キャッシュを更新
                queryClient.setQueryData<InquiryMessageWithSender[]>(
                  ["inquiryMessages", inquiryId],
                  (oldData) => {
                    if (!oldData) return [newMessage];
                    // 重複チェック (念のため)
                    if (oldData.some((msg) => msg.id === newMessage.id)) {
                      return oldData;
                    }
                    return [...oldData, newMessage];
                  }
                );
              } catch (e) {
                console.error("Failed to parse SSE message:", e);
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          console.error("SSE error:", error);
          // 再接続ロジックを入れるならここ
          setTimeout(connectSSE, 5000);
        }
      }
    };

    connectSSE();

    return () => {
      controller.abort();
    };
  }, [inquiryId, currentUser, queryClient]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    if (message.length > 600) {
      alert("メッセージは600文字以内で入力してください");
      return;
    }

    try {
      await sendMessageMutation.mutateAsync({ message: message.trim() });
      setMessage("");
    } catch (error: any) {
      alert(`メッセージの送信に失敗しました: ${error.message}`);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleUpdateStatus = async (newStatus: string) => {
    try {
      await updateStatusMutation.mutateAsync({ status: newStatus });
      setAnchorEl(null);
    } catch (error: any) {
      alert(`ステータスの更新に失敗しました: ${error.message}`);
    }
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessageId) return;

    try {
      await deleteMessageMutation.mutateAsync(selectedMessageId);
      setDeleteDialogOpen(false);
      setSelectedMessageId(null);
    } catch (error: any) {
      alert(`メッセージの削除に失敗しました: ${error.message}`);
    }
  };

  const openMessageMenu = (
    event: React.MouseEvent<HTMLElement>,
    messageId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessageId(messageId);
  };

  const closeMessageMenu = () => {
    setAnchorEl(null);
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    closeMessageMenu();
  };

  const getStatusColor = (
    status: string
  ): "default" | "primary" | "success" | "warning" => {
    switch (status) {
      case "open":
        return "primary";
      case "resolved":
        return "success";
      case "archived":
        return "default";
      default:
        return "warning";
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "open":
        return "対応中";
      case "resolved":
        return "解決済み";
      case "archived":
        return "アーカイブ";
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("ja-JP", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDisplayName = (msg: InquiryMessageWithSender): string => {
    // バックエンドで sender_display_name が実装されていない場合の仮実装
    if (msg.sender_display_name) return msg.sender_display_name;
    if (msg.sender_role === "staff") return "スタッフ";
    return "ユーザー";
  };

  if (inquiryLoading || messagesLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  if (inquiryError || messagesError) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          データの読み込みに失敗しました:{" "}
          {inquiryError?.message || messagesError?.message}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/inquiries")}
          sx={{ mt: 2 }}
        >
          一覧に戻る
        </Button>
      </Container>
    );
  }

  if (!inquiry) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">問い合わせが見つかりません</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/inquiries")}
          sx={{ mt: 2 }}
        >
          一覧に戻る
        </Button>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: 4,
        mb: 4,
        height: "calc(100vh - 200px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ヘッダー */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/inquiries")}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6">問い合わせ詳細</Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                <Chip
                  label={getStatusLabel(inquiry.status)}
                  color={getStatusColor(inquiry.status)}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
          {isStaffOrAdmin && (
            <Box>
              {inquiry.status === "open" && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleUpdateStatus("resolved")}
                  disabled={updateStatusMutation.isPending}
                >
                  解決済みにする
                </Button>
              )}
              {inquiry.status === "resolved" && (
                <Button
                  variant="outlined"
                  startIcon={<ReplayIcon />}
                  onClick={() => handleUpdateStatus("open")}
                  disabled={updateStatusMutation.isPending}
                >
                  再オープン
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* メッセージリスト */}
      <Paper
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          mb: 2,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "#f5f5f5" : "grey.900",
        }}
      >
        <Stack spacing={2}>
          {messages && messages.length > 0 ? (
            messages.map((msg: InquiryMessageWithSender) => {
              const isMyMessage = msg.sender_firebase_uid === userId;
              const isDeleted = msg.deleted_at !== null;

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    justifyContent: isMyMessage ? "flex-end" : "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: "70%",
                      position: "relative",
                    }}
                  >
                    {!isDeleted && (
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: isMyMessage
                            ? "primary.main"
                            : "background.paper",
                          color: isMyMessage ? "white" : "text.primary",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            {getDisplayName(msg)}
                          </Typography>
                          {isMyMessage && (
                            <IconButton
                              size="small"
                              sx={{ ml: 1, p: 0.5 }}
                              onClick={(e) => openMessageMenu(e, msg.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.5,
                            textAlign: "right",
                            opacity: 0.7,
                          }}
                        >
                          {formatDateTime(msg.created_at)}
                        </Typography>
                      </Paper>
                    )}
                    {isDeleted && (
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: "action.disabledBackground",
                          color: "text.disabled",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic" }}
                        >
                          このメッセージは削除されました
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Box>
              );
            })
          ) : (
            <Alert severity="info">
              メッセージがありません。最初のメッセージを送信しましょう。
            </Alert>
          )}
          <div ref={messagesEndRef} />
        </Stack>
      </Paper>

      {/* メッセージ入力フォーム */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="メッセージを入力 (最大600文字)"
            disabled={sendMessageMutation.isPending}
            helperText={`${message.length}/600文字`}
            error={message.length > 600}
          />
          <Button
            variant="contained"
            endIcon={<SendIcon />}
            onClick={handleSendMessage}
            disabled={
              !message.trim() ||
              message.length > 600 ||
              sendMessageMutation.isPending
            }
            sx={{ minWidth: "100px", height: "56px" }}
          >
            送信
          </Button>
        </Box>
      </Paper>

      {/* メッセージメニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMessageMenu}
      >
        <MenuItem onClick={openDeleteDialog}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          削除
        </MenuItem>
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>メッセージを削除</DialogTitle>
        <DialogContent>
          <Typography>
            このメッセージを削除してもよろしいですか?この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          <Button
            onClick={handleDeleteMessage}
            color="error"
            disabled={deleteMessageMutation.isPending}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InquiryChatPage;
